"use client";

/**
 * Home, answering the three questions a creator opens the app with.
 *
 * In order: is anything waiting on me, what am I getting paid, and how do I
 * send the next one. The shipped home opened with a program eyebrow, a welcome
 * line, the payout formula as a sentence and two CapCut links — four blocks of
 * prose before a single number or action. The tutorials are still here; they
 * are two taps down, beside the brief, which is where someone about to film
 * goes looking for them.
 *
 * The money is two figures rather than one total, and the coming one leads.
 * "Earnings: $870" answers neither "what did I get" nor "what is owed", and
 * owed is the reason people check.
 *
 * Promoted from the prototype, where it read its fixtures directly. What
 * changed is the interface, not the drawing: the fixtures became props, and
 * the two places the product has something the prototype never did — real ad
 * media with a lightbox, and the handles and script-check cards — are slots the
 * host fills.
 */

import { AlertTriangle, MessageCircle, Plus } from "lucide-react";
import type { ReactNode } from "react";
import { PageHeader } from "@/components/ui/design-system/page-header";
import {
  CardTitle,
  Hint,
  Label,
  SectionTitle,
  Strong,
} from "@/components/ui/design-system/text";
import { useCreatorTranslations } from "@/i18n/creator/useCreatorTranslations";
import { cn } from "@/lib/utils";
import { formatUsd } from "../formatUsd";
import { PROSE, SECTION_CAP_TRIM } from "./chrome";
import { EarningsCard } from "./EarningsCard";
import { ScriptReviewCard } from "./ScriptReviewCard";
import { CARD_ROW, CARD_ROW_WARN, Card, Primary, Tile } from "./ui";

export type HomeDoc = {
  id: string;
  title: string;
  detail?: string;
  /**
   * The mark beside it, so both panes on this page look like one set.
   *
   * Without it the documents are the only rows here with no icon, which reads
   * as the pane below being more important rather than as a different stage of
   * the same job. `Row` gives every icon the same tile and the same colour —
   * none of them takes a tint of its own.
   */
  icon?: ReactNode;
};

/** A card in the bento that is not the hero. */
type DestinationCard = {
  key: string;
  title: string;
  /** Optional, because `HomeDoc` allows a document with no summary line. */
  detail?: string;
  icon?: ReactNode;
  onClick?: () => void;
};

export function HomeScreen({
  firstName,
  needsYou,
  needsYouDetail,
  onNeedsYou,
  outstandingUsd,
  paidUsd,
  payoutCapNotice,
  nextPayoutOn,
  payoutRequestMinimumUsd,
  payoutSpendSharePct,
  onSubmit,
  submitDisabled,
  topAds,
  docs,
  onOpenDoc,
  onCheckScript,
  onMessageTeam,
  openRequests,
  notice,
  headerActions,
  account,
  gutter,
}: {
  firstName: string;
  /** How many videos are waiting on a new take from this creator. */
  needsYou: number;
  /** The one-line "why", when the host knows it. */
  needsYouDetail?: string;
  onNeedsYou: () => void;
  outstandingUsd: number;
  paidUsd: number;
  /** The earnings card's monthly-cap line, from `usePayoutCapNotice`. */
  payoutCapNotice?: string;
  /**
   * When the outstanding figure is expected to land, if that is known.
   *
   * Optional because it is not, yet: nothing on the creator side returns a
   * payout date, and a made-up one on a money figure is worse than none. Left
   * out, the figure is labelled plainly instead of with a date.
   */
  nextPayoutOn?: string;
  payoutRequestMinimumUsd: number;
  payoutSpendSharePct: number;
  onSubmit: () => void;
  /**
   * Holds the submit button, while the program takes no new videos.
   *
   * Disabled rather than hidden: the card keeps its place in the row, and the
   * host says why in `notice`.
   */
  submitDisabled?: boolean;
  /**
   * The "what's working" row, supplied by the host.
   *
   * A slot because the two hosts have genuinely different media: the prototype
   * draws a wash from an id, and the product has real video that plays on
   * hover and opens in a lightbox with sound. The section's heading and the
   * argument for it live here; the tiles come from whoever has the files.
   */
  topAds?: ReactNode;
  docs: HomeDoc[];
  onOpenDoc: (id: string) => void;
  onCheckScript: () => void;
  onMessageTeam: () => void;
  /**
   * The open requests preview, supplied by the host.
   *
   * Directly under the three cards, above what's working. Requests is the
   * second most visited screen after this one and the card is Home's way
   * into it, so it sits with the cards a creator opens the app for rather
   * than at the foot of the page, under a shelf of videos and a section of
   * documents.
   */
  openRequests?: ReactNode;
  /**
   * A row above the three cards, for something the creator has to fix before
   * the rest of the page is much use — today, a missing handle — or has to
   * know before reaching for the submit button: a program that has paused
   * new videos.
   *
   * Above them rather than in the header's `actions` slot, where it started.
   * That slot is a 380-point column beside the greeting, which wrapped two
   * sentences into four lines; the page's own column is twice that and the
   * thing it is asking about is a card exactly like the ones underneath.
   */
  notice?: ReactNode;
  /**
   * Beside the greeting, past rail width only.
   *
   * `PageHeader` stacks its `actions` under the title on a narrow screen,
   * which is not what these are for — they are the quiet way out of the page
   * and on a phone the footer already carries them. Hidden rather than
   * rearranged, so the header on a phone stays a greeting.
   */
  headerActions?: ReactNode;
  /**
   * Who you are signed in as, for hosts with nowhere else to put it. The rail
   * carries it past its own width, like every desktop app.
   */
  account?: ReactNode;
  /**
   * Side padding for the inset blocks, when the host does not already provide
   * it on the page.
   *
   * Per block rather than on the wrapper, because the "what's working" row is
   * a horizontal scroller on a phone and has to bleed to the screen edge — a
   * scroller that stops short of the edge reads as a list that ended.
   */
  gutter?: string;
}) {
  const t = useCreatorTranslations("home");
  /**
   * What to read before filming: the documents this program has, or the one
   * card that stands in for them.
   *
   * Built as a list rather than written out in the grid because the layout
   * has to know how many there are — see the grid below.
   */
  const readingCards: DestinationCard[] = docs.length
    ? docs.map((doc) => ({
        key: doc.id,
        title: doc.title,
        detail: doc.detail,
        icon: doc.icon,
        onClick: () => onOpenDoc(doc.id),
      }))
    : [
        // States a fact rather than offering a document and refusing: no
        // chevron and no press, so it says the brief is coming instead of
        // looking like a door that does not open.
        {
          key: "no-docs",
          title: t("noDocs.title"),
          detail: t("noDocs.detail"),
        },
      ];
  const inboxCard: DestinationCard = {
    key: "inbox",
    title: t("messageTeam.title"),
    detail: t("messageTeam.detail"),
    // 20, not the 18 a row's plate holds: bare on a tile there is no
    // plate to give it presence, so it carries its own.
    icon: <MessageCircle className="size-[20px]" strokeWidth={2} />,
    onClick: onMessageTeam,
  };
  const destinationTile = (card: DestinationCard, placement: string) => (
    <Tile
      key={card.key}
      className={cn(CARD_ROW, placement)}
      icon={card.icon}
      title={card.title}
      detail={card.detail}
      onClick={card.onClick}
    />
  );

  return (
    /*
      `section` between the sections, `block` inside them — the two spacing
      roles the design system already names. This was `block` at both levels
      and a hand-picked `8` at desk width, so the gap between "What's working"
      and the cards above it was the same 24 points as the gap between two
      cards in one row, and nothing on the page said where one section ended.
    */
    /*
      `card` between sections on a phone, `section` past rail width.

      The role is one number and the screens are not: 48 points is about a
      twentieth of the 1104-point column and about an eighth of a 390-point
      one, so the same gap that separates two blocks at a desk reads as a
      hole under a greeting on a phone — most visibly with a count waiting,
      where the header is a single line and then nothing.

      A step down the same scale rather than a number, so the two widths are
      still speaking the roles.
    */
    <div className="flex flex-col gap-nf-ds-card py-nf-ds-4 lg:gap-nf-ds-section lg:py-nf-ds-6">
      {/* The product's own page header, not a local one. It is what keeps
          this screen's title identical to every Framework page's — the reason
          it went wrong here is that Home hand-wrote a heading instead of
          reaching for the component that already existed. */}
      <div className={gutter}>
        {/*
          THE STANDFIRST ONLY SPEAKS WHEN NOTHING ELSE DOES.

          It used to read "2 videos need a new take." with a card directly
          under it titled "Read the notes and send new takes" — the same fact
          twice, in nearly the same words, one of them a statement and one of
          them the button for it. The count is the only thing the line carried
          that the card did not, so the count moved into the card and the line
          went.

          Not dropped outright: with nothing waiting there is no card, and
          then this is the only thing on the page that answers the question
          somebody opened it to ask. A greeting alone would leave that unsaid.
        */}
        <PageHeader
          actions={
            headerActions ? (
              <div className="hidden items-center gap-nf-ds-2 lg:flex">
                {headerActions}
              </div>
            ) : null
          }
          title={t("greeting", { name: firstName })}
          subtitle={needsYou > 0 ? undefined : t("nothingWaiting")}
        />
      </div>
      {/*
        Three cards, each answering one question, on one line where there is
        room for it.

        THEY WERE TWO. What you are owed and how you send the next one lived in
        one pane, on the argument that they are one sentence — "here is what
        you have, here is how you add to it". They are not. One is a figure you
        read and the other is a thing you do, and the pane had upload file
        formats sitting beside a balance. The reason they were merged was a
        layout problem, not a content one: a card beside a bare button column
        left a hole under the button. Two real cards of equal height solve that
        without pretending the contents belong together.

        The grid is the whole responsive rule. One column on a phone, and past
        rail width as many columns as there are cards — so the pair becomes a
        trio when a note is waiting, and nothing has to be kept in step by
        hand. Equal height comes from the grid rather than from any card
        guessing a number.
      */}
      {notice ? <div className={gutter}>{notice}</div> : null}

      <div
        className={cn(
          "grid gap-nf-ds-block",
          needsYou > 0 ? "lg:grid-cols-3" : "lg:grid-cols-2",
          gutter,
        )}
      >
        {/*
          THE CARD IS THE BUTTON. It was a `ListCard` with one `Row` inside it,
          and a list of one is not a list: the row sat 4 points in from the
          card's edge with a tighter corner, so pressing filled a rectangle
          *inside* the card and drew a frame around itself — and the scale
          shrank that fill while the card behind it stayed put, which is the
          border that appeared on touch.

          One element instead. The press fills the whole surface and the whole
          surface moves, because there is no longer anything behind it to
          stay still.
        */}
        {needsYou > 0 ? (
          // `Tile`, like the documents below it: glyph, name, line, sitting
          // on the card's floor. It was a `Row` — the horizontal shape with
          // the glyph in a plate and a chevron at the end — which made the
          // one card on this screen that is a notification look unlike the
          // cards it is a sibling of.
          //
          // The glyph keeps the warn tone rather than taking the title's
          // colour the way a plain tile's does. The card is tinted; a
          // neutral mark in an amber card is the one part of it not saying
          // the same thing as the rest.
          <Tile
            className={CARD_ROW_WARN}
            icon={
              <AlertTriangle
                className="size-[20px] text-nf-ds-tone-warn-fg"
                strokeWidth={2}
              />
            }
            title={t("needsYou.title", { count: needsYou })}
            detail={needsYouDetail}
            onClick={onNeedsYou}
          />
        ) : null}

        <EarningsCard
          outstandingUsd={outstandingUsd}
          paidUsd={paidUsd}
          payoutRequestMinimumUsd={payoutRequestMinimumUsd}
          payoutSpendSharePct={payoutSpendSharePct}
          capNotice={payoutCapNotice}
        />

        {/*
          The action, with its own card and its own heading.

          A heading over a button rather than a bare button, because the other
          cards on this line have one and a card that starts with a control
          reads as a fragment beside them. The file line stays with the button
          it describes, which is where it was always meant to be — beside a
          balance it was a non-sequitur.
        */}
        <Card className="flex flex-col justify-between gap-nf-ds-5">
          {/* `item` between the title and its line, like every other card. */}
          <div className="flex flex-col gap-nf-ds-item">
            {/* The card-title role, like every other card on this screen.
                It was `PROSE` plus `font-[600]`, which is body text with a
                weight bolted on — and a different weight from the rows next
                to it. */}
            <CardTitle>{t("submit.title")}</CardTitle>
            <Hint muted className={cn(PROSE, "text-pretty")}>
              {t("submit.detail")}
            </Hint>
          </div>

          {/* Full width at every size, which is the one place `Primary`'s own
              rule does not apply. That rule shrinks a button at desk width so
              it is sized to its label rather than to the screen — but this one
              is not spanning a screen, it is spanning the card it is the point
              of. A card whose whole reason to exist is one action should not
              have that action tucked into a corner of it. */}
          <Primary
            className="lg:w-full"
            onClick={onSubmit}
            disabled={submitDisabled}
            // `Plus`, the same glyph the rail and the tab bar put on Submit.
            // One destination, one mark: an upload arrow here and a plus in
            // the nav read as two different things to reach for.
            icon={<Plus className="size-[18px]" strokeWidth={2.2} />}
          >
            {t("submit.action")}
          </Primary>
        </Card>
      </div>
      {/* Guarded like `notice`, and for the same reason: an unconditional
          wrapper round an absent slot is a zero-height flex child that still
          takes a gap on both sides. The prototype passes nothing here. */}
      {openRequests ? <div className={gutter}>{openRequests}</div> : null}
      {/* No heading here: the shelf carries its own, and a section wrapping it
          with a second one is how "What's working" ended up sitting above
          "Top ads", each with its own paragraph saying the same thing. */}
      {topAds ? (
        <div className={cn(gutter, SECTION_CAP_TRIM)}>{topAds}</div>
      ) : null}
      {/*
        TWO SECTIONS, ONE UNDER THE OTHER.

        They were side by side, which put two headings and four destinations on
        one line and made the page look like it had a column layout it does not
        have anywhere else. Each is a stage of the same job — before you film,
        before you post — and reading them in order is the point. The rows
        inside each go across instead, which is where the width went.
      */}
      {/*
        ONE SECTION, NOT TWO.

        This was "Before you film" over the documents and "Before you post"
        over the checker and the inbox, and the split did not survive its own
        contents: the script checker's first words are "Paste a script before
        you film", and it was sitting under Before you post. A taxonomy whose
        members contradict their heading is not describing anything — it was
        two headings, two section gaps and a reading order for four links that
        are all the same thing, places to go before you shoot.

        The layout is what the split was paying for and it comes free here:
        one grid, the hero across two of its four columns, the destinations
        filling the rest.
      */}
      <section
        className={cn("flex flex-col gap-nf-ds-item", gutter, SECTION_CAP_TRIM)}
      >
        <SectionTitle>{t("beforeYouFilm")}</SectionTitle>

        {/*
          A BENTO, SIZED TO WHAT IS ACTUALLY IN IT.

          Four columns with the hero across two, the way the reference lays
          it out. The reference fills that grid because it has seven tiles;
          this section has three or four, so the same grid left holes — and
          at the lower count it stranded the last card alone in a row, which
          is the state a program whose brief is not written yet is in.

          THE READING COMES FIRST, THEN THE CHECKER. The checker reviews a
          script against the brief and the compliance rules, so those are
          what a creator reads before pasting one in. Message the team stays
          last.

          Message the team takes the rest of its row when the count does not
          divide. With both documents that is the full width under a tidy
          row of two squares and the hero; with one or none, everything lands
          in a single row and nothing spans. At two columns the hero takes a
          row of its own, so a lone card before it and the inbox after it
          each take the whole row rather than leaving a cell empty beside
          them.
        */}
        <div className="grid items-stretch gap-nf-ds-block md:grid-cols-2 lg:grid-cols-4">
          {readingCards.map((card, index) =>
            destinationTile(
              card,
              cn(
                // SQUARE AT RAIL WIDTH, which is what sets the row's height —
                // one column of the four is 258 points, so the tiles are 258
                // tall and the hero stretches to meet them. A number here
                // instead would be the same measurement written down twice,
                // and wrong the day the column count or the gap changes.
                "lg:aspect-square",
                index === readingCards.length - 1 &&
                  readingCards.length % 2 === 1 &&
                  "md:col-span-2 lg:col-span-1",
              ),
            ),
          )}

          <ScriptReviewCard onOpen={onCheckScript} className="md:col-span-2" />

          {destinationTile(
            inboxCard,
            readingCards.length === 2
              ? // A height at rail width, because a card the width of the
                // page with two lines in it is a strip. The squares beside
                // the hero get their height from being square and this one
                // has nothing to take height from, so it is the one place a
                // number is unavoidable — enough for the content to sit on a
                // floor with air above it, short of the 258 that would make
                // it a slab. Never square: a square 1104 points wide is a
                // page, not a card.
                "md:col-span-2 lg:col-span-4 lg:min-h-[200px]"
              : "md:col-span-2 lg:col-span-1 lg:aspect-square",
          )}
        </div>
      </section>

      {account ? (
        <div className={cn("flex flex-col gap-nf-ds-1 pt-nf-ds-2", gutter)}>
          {account}
        </div>
      ) : null}
    </div>
  );
}
